import { useEffect, useState } from "react";
import { CheckCircle2, Star, ThumbsDown, ThumbsUp, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import CriticalPinDialog from "../CriticalPinDialog";

export default function ComplaintsTab() {
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(null);
    const [pinTarget, setPinTarget] = useState(null);
    const load = () => api.get("/complaints").then((r) => setItems(r.data)).catch(() => setItems([]));
    useEffect(() => { load(); }, []);

    const resolve = async (c) => {
        setBusy(c.complaint_id);
        try {
            await api.post(`/complaints/${c.complaint_id}/resolve`);
            toast.success("Kayıt tamamlandı olarak işaretlendi (pasife alındı)");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        } finally {
            setBusy(null);
        }
    };

    const del = async (pin) => {
        try {
            await api.delete(`/admin/records/complaints/${pinTarget.complaint_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Kayıt kalıcı olarak silindi");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    if (!items) return <div className="h-48 animate-pulse rounded-2xl bg-muted" />;

    return (
        <div data-testid="complaints-tab">
            <p className="mb-4 text-sm text-muted-foreground">Mağaza ve tedarikçilerin birbirleri hakkındaki şikayet ve ödül kayıtları.</p>
            <div className="space-y-3">
                {items.map((c) => (
                    <div key={c.complaint_id} className={`rounded-2xl border bg-card p-4 shadow-sm ${c.status === "passive" ? "opacity-60" : ""}`} data-testid={`complaint-row-${c.complaint_id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                {c.kind === "complaint" ? <ThumbsDown className="h-5 w-5 text-red-500" /> : <ThumbsUp className="h-5 w-5 text-emerald-600" />}
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="font-semibold">{c.target_name}</p>
                                        <Badge variant="outline" className={`text-[10px] ${c.kind === "complaint" ? "border-red-200 bg-red-100 text-red-700" : "border-emerald-200 bg-emerald-100 text-emerald-800"}`}>
                                            {c.kind === "complaint" ? "Şikayet" : "Ödül"}
                                        </Badge>
                                        <span className="flex items-center gap-0.5 text-xs font-bold text-saffron-500">
                                            {[...Array(c.rating)].map((_, i) => <Star key={i} className="h-3 w-3 fill-saffron-400" />)}
                                        </span>
                                    </div>
                                    <p className="mt-0.5 text-xs text-muted-foreground">{c.from_name} ({c.from_role === "buyer" ? "Mağaza" : "Tedarikçi"}) • {fmtDate(c.created_at)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`text-[10px] ${c.status === "active" ? "border-amber-200 bg-amber-100 text-amber-800" : "border-stone-200 bg-stone-100 text-stone-600"}`}>
                                    {c.status === "active" ? "Açık" : "Tamamlandı"}
                                </Badge>
                                {c.status === "active" && (
                                    <Button size="sm" variant="outline" disabled={busy === c.complaint_id} onClick={() => resolve(c)} data-testid={`complaint-resolve-${c.complaint_id}`}>
                                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Tamamlandı
                                    </Button>
                                )}
                                <Button size="sm" variant="outline" className="text-destructive" disabled={busy === c.complaint_id} onClick={() => setPinTarget(c)} data-testid={`complaint-delete-${c.complaint_id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">{c.description}</p>
                    </div>
                ))}
                {!items.length && <p className="rounded-2xl border border-dashed p-16 text-center text-sm text-muted-foreground" data-testid="complaints-empty">Kayıt yok.</p>}
            </div>
            <CriticalPinDialog open={!!pinTarget} onClose={() => setPinTarget(null)} onSubmit={del} actionLabel={pinTarget ? `Şikayet/ödül kaydı silme: ${pinTarget.target_name}` : ""} />
        </div>
    );
}
